/**
 * Notification Settings Page
 * ==========================
 * User preferences for notifications including sound, email, and in-app notifications.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Bell,
  Volume2,
  Mail,
  MessageSquare,
  Calendar,
  ClipboardList,
  FileText,
  LifeBuoy,
  Save,
  Loader2,
  Check,
  ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// Default notification preferences
const DEFAULT_PREFERENCES = {
  sound_enabled: true,
  sound_volume: 50,
  
  // In-app notifications
  notes_assigned: true,
  tasks_assigned: true,
  tasks_due: true,
  calendar_reminders: true,
  chat_messages: true,
  support_replies: true,
  
  // Email notifications
  email_daily_digest: false,
  email_mentions: true,
  email_task_reminders: true,
  
  // Quiet hours
  quiet_hours_enabled: false,
  quiet_hours_start: '22:00',
  quiet_hours_end: '08:00',
};

const NotificationSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Fetch user notification preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await axios.get(`${API}/api/users/me/notification-preferences`);
        setPreferences({ ...DEFAULT_PREFERENCES, ...response.data });
      } catch (err) {
        // Use defaults if no preferences saved yet
        console.log('Using default notification preferences');
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  }, []);
  
  // Handle preference change
  const handleChange = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };
  
  // Save preferences
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/users/me/notification-preferences`, preferences);
      toast.success('Notification preferences saved');
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving preferences:', err);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };
  
  // Test notification sound
  const testSound = () => {
    if (preferences.sound_enabled) {
      // Play a test notification sound
      const audio = new Audio('/sounds/notification.mp3');
      audio.volume = preferences.sound_volume / 100;
      audio.play().catch(() => {
        // Fallback to web audio API
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          oscillator.frequency.value = 800;
          oscillator.type = 'sine';
          gainNode.gain.value = preferences.sound_volume / 100 * 0.3;
          oscillator.start();
          setTimeout(() => oscillator.stop(), 200);
        } catch (e) {
          console.log('Could not play test sound');
        }
      });
      toast.success('Test notification played');
    } else {
      toast.info('Sound notifications are disabled');
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Bell className="h-6 w-6" />
                Notification Settings
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage how and when you receive notifications
              </p>
            </div>
          </div>
          
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="gap-2"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : hasChanges ? (
              <Save className="h-4 w-4" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
          </Button>
        </div>
        
        {/* Sound Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Volume2 className="h-5 w-5" />
              Sound Notifications
            </CardTitle>
            <CardDescription>
              Control notification sounds and volume
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Sound Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Play a sound when you receive a notification
                </p>
              </div>
              <Switch
                checked={preferences.sound_enabled}
                onCheckedChange={(checked) => handleChange('sound_enabled', checked)}
              />
            </div>
            
            {preferences.sound_enabled && (
              <>
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Volume</Label>
                    <p className="text-sm text-muted-foreground">
                      Notification sound volume level
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={preferences.sound_volume}
                      onChange={(e) => handleChange('sound_volume', parseInt(e.target.value))}
                      className="w-32"
                    />
                    <span className="text-sm w-12 text-right">{preferences.sound_volume}%</span>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={testSound}>
                  Test Sound
                </Button>
              </>
            )}
          </CardContent>
        </Card>
        
        {/* In-App Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              In-App Notifications
            </CardTitle>
            <CardDescription>
              Choose which events trigger notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label>Notes Assigned</Label>
                  <p className="text-xs text-muted-foreground">
                    When someone assigns you a note
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.notes_assigned}
                onCheckedChange={(checked) => handleChange('notes_assigned', checked)}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label>Tasks Assigned</Label>
                  <p className="text-xs text-muted-foreground">
                    When someone assigns you a task
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.tasks_assigned}
                onCheckedChange={(checked) => handleChange('tasks_assigned', checked)}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label>Task Due Reminders</Label>
                  <p className="text-xs text-muted-foreground">
                    When a task is due or overdue
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.tasks_due}
                onCheckedChange={(checked) => handleChange('tasks_due', checked)}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label>Calendar Reminders</Label>
                  <p className="text-xs text-muted-foreground">
                    Event reminders from your calendar
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.calendar_reminders}
                onCheckedChange={(checked) => handleChange('calendar_reminders', checked)}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label>Chat Messages</Label>
                  <p className="text-xs text-muted-foreground">
                    New messages in chat
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.chat_messages}
                onCheckedChange={(checked) => handleChange('chat_messages', checked)}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LifeBuoy className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label>Support Ticket Replies</Label>
                  <p className="text-xs text-muted-foreground">
                    Responses to your support tickets
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.support_replies}
                onCheckedChange={(checked) => handleChange('support_replies', checked)}
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Quiet Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Quiet Hours
              <Badge variant="outline" className="ml-2">Optional</Badge>
            </CardTitle>
            <CardDescription>
              Pause notifications during specific hours
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Quiet Hours</Label>
                <p className="text-sm text-muted-foreground">
                  Mute notifications during set hours
                </p>
              </div>
              <Switch
                checked={preferences.quiet_hours_enabled}
                onCheckedChange={(checked) => handleChange('quiet_hours_enabled', checked)}
              />
            </div>
            
            {preferences.quiet_hours_enabled && (
              <>
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Select
                      value={preferences.quiet_hours_start}
                      onValueChange={(value) => handleChange('quiet_hours_start', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = i.toString().padStart(2, '0');
                          return (
                            <SelectItem key={hour} value={`${hour}:00`}>
                              {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Select
                      value={preferences.quiet_hours_end}
                      onValueChange={(value) => handleChange('quiet_hours_end', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = i.toString().padStart(2, '0');
                          return (
                            <SelectItem key={hour} value={`${hour}:00`}>
                              {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        
        {/* Email Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Notifications
              <Badge variant="outline" className="ml-2">Coming Soon</Badge>
            </CardTitle>
            <CardDescription>
              Receive notifications via email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 opacity-50 pointer-events-none">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Daily Digest</Label>
                <p className="text-sm text-muted-foreground">
                  Receive a daily summary of activity
                </p>
              </div>
              <Switch
                checked={preferences.email_daily_digest}
                disabled
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Mentions</Label>
                <p className="text-sm text-muted-foreground">
                  When someone mentions you
                </p>
              </div>
              <Switch
                checked={preferences.email_mentions}
                disabled
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NotificationSettings;
